import { get } from 'lodash-es'
import { EPI_SUITE_URL } from '@/main'
import Axios from 'axios'
import $RefParser from '@apidevtools/json-schema-ref-parser'
import { setupCache } from 'axios-cache-interceptor'
import { cachedAxiosOptions } from '@/utils/cachedAxiosOptions'
import { groupBy, camelCase } from 'lodash-es'
import { getErrorMessage } from '@/main'
import { getChemicalUniqueKey } from '@/utils/chemical'
import { validateSmilesBasic } from '@/utils/responseNormalizers'
import { modelStateRegistry } from '@/utils/modelStateRegistry'
import { metadata as hardcodedMetadata } from '@/components/combined-predict/models/epiSuite/hardcodedMetadata'

const epiSuiteState = get(modelStateRegistry, 'epiSuite')
const keys = epiSuiteState.keys

const axiosInstance = Axios.create()
const cachedAxios = setupCache(axiosInstance)

const state = {
  ...epiSuiteState.createState(),
}

const getters = {
  ...epiSuiteState.createGetters(),
}

const mutations = {
  ...epiSuiteState.createMutations(),
}

const actions = {
  estimateForStructures: async (context, chemicals) => fetchEpiSuiteEstimateForStructures(context, chemicals),
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const createObjectFromModelsArray = (modelsArray) => {
  const groupedModels = groupBy(modelsArray, (model) => camelCase(model.name))
  return Object.entries(groupedModels).reduce((acc, [key, models]) => {
    acc[key] = models[0]
    return acc
  }, {})
}

const createObjectFromModelResultWhenArrayWithLengthOne = (modelResult) =>
  Object.entries(modelResult).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.filter(Boolean).length === 1) {
      acc[key] = value[0]
    } else {
      acc[key] = value
    }
    return acc
  }, {})

function parseEpiSuiteData(data) {
  const predictions = []

  for (const [key, value] of Object.entries(data))
    if (value['estimatedValue'] || value['selectedValue']) {
      predictions.push({
        ...value,
        property: key,
        experimental: value['experimentalValues'],
        predicted: value['estimatedValue'],
        selectedValue: value['selectedValue'],
        parameters: value['parameters'],
      })
    } else if (value['models']) {
      predictions.push({
        ...value,
        property: key,
        experimental: null,
        predicted: {
          model: createObjectFromModelsArray(value['models']),
          // models: value['models'],
          output: value['output'],
        },
        parameters: value['parameters'],
      })
    } else if (value['model']) {
      predictions.push({
        ...value,
        property: key,
        model: createObjectFromModelResultWhenArrayWithLengthOne(value['model']),
        parameters: value['parameters'],
      })
    } else if (typeof value === 'object') {
      predictions.push({
        property: key,
        ...value,
      })
    }

  return predictions
}

const getFirstLineOfString = (string) => {
  if (string) {
    return string.split(/\r?\n/)[0]
  }
  return ''
}

const validateSmiles = (smiles) => {
  const basicValidationError = validateSmilesBasic(smiles)
  if (basicValidationError) return basicValidationError
  if (smiles.includes('.')) return 'Salts and multi-component chemicals are not supported'
  return null
}

const validateStructuresForEpiSuite = (chemicals) =>
  chemicals.map((chemical) => {
    return { chemical: { ...chemical, id: getChemicalUniqueKey(chemical) }, error: validateSmiles(chemical.smiles) }
  })

const filterAlreadyFetchedChemicals = (chemicals, existingResults) => {
  const existingSmiles = new Set(existingResults.map((result) => getChemicalUniqueKey(result.chemical)))
  return chemicals.filter((chemical) => !existingSmiles.has(getChemicalUniqueKey(chemical)))
}

const constructEpiSuiteEstimateResult = ({ chemical, predictions, error }) => ({
  chemical,
  predictions,
  error,
})

export function getEpiSuiteEstimateErrors(results) {
  if (!results || results.length === 0) return []
  return results
    .filter((result) => result.error)
    .map((result) => ({ chemical: result.chemical, message: result.error }))
}

async function fetchEpiSuiteEstimateForStructures(context, chemicals, batchSize = 20) {
  const { state, commit, getters } = context

  commit(keys.SET_PROGRESS, 0)
  commit(keys.SET_LOADING, true)

  const incomingSmilesSet = new Set(chemicals.map((chemical) => getChemicalUniqueKey(chemical)))
  commit(keys.FILTER_RESULTS_BY_SMILES_SET, incomingSmilesSet)

  const existingResults = state.results
  const chemicalsToConsider = filterAlreadyFetchedChemicals(chemicals, existingResults)
  const validatedStructuresToFetch = validateStructuresForEpiSuite(chemicalsToConsider)

  // Separate validation errors and valid items
  const validationErrors = validatedStructuresToFetch.filter(({ error }) => error)
  const chemicalsToFetch = validatedStructuresToFetch.filter(({ error }) => !error)

  // First, commit all validation errors so the user gets immediate feedback on those without having to wait for all the fetches to complete
  validationErrors.forEach(({ chemical, error }) => {
    commit(keys.PUSH_RESULT, constructEpiSuiteEstimateResult({ chemical, predictions: [], error }))
  })

  const totalToFetch = chemicalsToFetch.length

  let fetchedCount = 0

  // Create AbortController to cancel all pending requests
  const abortController = new AbortController()
  commit(keys.SET_ABORT_CONTROLLER, abortController)

  const BATCH_SIZE = batchSize

  for (let i = 0; i < chemicalsToFetch.length; i += BATCH_SIZE) {
    if (abortController?.signal?.aborted) {
      break
    }

    const batch = chemicalsToFetch.slice(i, i + BATCH_SIZE)

    const promises = batch.map(({ chemical }) => {
      return cachedAxios
        .get(`${EPI_SUITE_URL}/submit?smiles=${encodeURIComponent(chemical.smiles)}`, {
          signal: abortController.signal, // Pass abort signal to axios
          ...cachedAxiosOptions,
          id: `api-science-data-experts-epi-suite-submit-${chemical.smiles}`,
        })
        .then((response) => {
          if (typeof response.data === 'string') {
            if (response.data.startsWith('Error')) throw new Error(getFirstLineOfString(response.data))
          }

          const predictions = parseEpiSuiteData(response.data)
          commit(keys.PUSH_RESULT, constructEpiSuiteEstimateResult({ chemical, predictions }))
        })
        .catch((fetchError) => {
          if (fetchError.name === 'AbortError' || fetchError.name === 'CanceledError') {
            return
          }

          console.warn('Epi Suite error:', fetchError)

          commit(
            keys.PUSH_RESULT,
            constructEpiSuiteEstimateResult({
              chemical,
              predictions: [],
              error: fetchError?.message,
            }),
          )
        })
        .finally(() => {
          fetchedCount++
          commit(keys.SET_PROGRESS, (fetchedCount / totalToFetch) * 100)
        })
    })

    await Promise.allSettled(promises)
    await sleep(0)
  }

  commit(keys.SET_ABORT_CONTROLLER, null)
  commit(keys.SET_LOADING, false)
  commit(keys.SET_PROGRESS, null)
  return getters[keys.getResults]
}

const parseEpiSuiteMetadata = (metadata) =>
  $RefParser
    .dereference(metadata)
    .then((dereferencedSchema) => dereferencedSchema)
    .catch((error) => {
      console.warn('Error dereferencing Epi Suite metadata schema:', getErrorMessage(error))
      return metadata
    })

export function prepareEpiSuiteMetadata(context) {
  const { commit } = context
  return Axios.get(`${EPI_SUITE_URL}/metadata`)
    .then((response) => parseEpiSuiteMetadata(response.data?.epiSuiteMetadata))
    .then((parsedMetadata) => {
      commit(`epiSuite/${keys.SET_METADATA}`, parsedMetadata)
      commit('setFeatures', { epiSuite: { category: 'service' } })
      return parsedMetadata
    })
    .catch((error) => {
      console.warn('Epi Suite metadata is not available: %o', getErrorMessage(error))

      if (!hardcodedMetadata) {
        commit('removeFeature', { epiSuite: {} })
        return null
      }

      return parseEpiSuiteMetadata(hardcodedMetadata).then((parsedMetadata) => {
        commit(`epiSuite/${keys.SET_METADATA}`, parsedMetadata)
        commit('setFeatures', { epiSuite: { category: 'service' } })
        return parsedMetadata
      })
    })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
